import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import MultiSelectFilter from '../../components/workspace/MultiSelectFilter';
import { useReportScope } from '../../lib/useReportScope';
import ReportPdfButton, { ExcelIcon, PdfIcon, excelBtnStyle, downloadExcel } from '../../components/workspace/ReportPdfButton';

const GOAL_STATUS_LABEL = {
  no_goals:      'No Goals',
  pending:       'Draft',
  needs_approval:'Awaiting Approval',
  approved:      'Approved',
};

const GOAL_STATUS_COLOR = {
  no_goals:      '#94a3b8',
  pending:       '#f0a93b',
  needs_approval:'#6f7bff',
  approved:      '#4ad295',
};

const labelStyle = {
  fontSize: 11, fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

function StatusBadge({ status }) {
  const color = GOAL_STATUS_COLOR[status] || '#6b7280';
  const label = GOAL_STATUS_LABEL[status] || status;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      background: `${color}22`, color,
    }}>
      {label}
    </span>
  );
}

function CountBadge({ value, color }) {
  return (
    <span style={{
      display: 'inline-block', minWidth: 28, textAlign: 'center',
      padding: '2px 8px', borderRadius: 10,
      fontSize: 12, fontWeight: 700,
      background: value > 0 ? `${color}18` : 'transparent',
      color: value > 0 ? color : 'var(--text-muted)',
    }}>
      {value}
    </span>
  );
}

function Th({ children, right }) {
  return (
    <th style={{
      padding: '10px 14px', textAlign: right ? 'right' : 'left',
      fontWeight: 600, color: 'var(--text-secondary)',
      whiteSpace: 'nowrap', fontSize: 12,
      textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>
      {children}
    </th>
  );
}

export default function GoalsReport() {
  const { appId } = useParams();
  const api = useApi();
  const { isAdmin, scopeIds } = useReportScope(appId);

  const [cycles,        setCycles]        = useState([]);
  const [selectedCycle, setSelectedCycle] = useState('');
  const [goalRows,      setGoalRows]      = useState([]);
  const [filterMember,  setFilterMember]  = useState([]);
  const [filterStatus,  setFilterStatus]  = useState('');
  const [loading,       setLoading]       = useState(false);
  const [cyclesLoading, setCyclesLoading] = useState(true);
  const [error,         setError]         = useState(null);

  useEffect(() => {
    api.listReviewCycles(appId)
      .then(r => {
        const items = r.items || [];
        setCycles(items);
        if (items.length > 0) {
          const today = new Date().toISOString().slice(0, 10);
          const active = items.find(c => c.start_date <= today && c.end_date >= today) || items[items.length - 1];
          setSelectedCycle(String(active.id));
        }
      })
      .catch(() => {})
      .finally(() => setCyclesLoading(false));
  }, [appId]);

  const loadGoals = useCallback(async () => {
    if (!selectedCycle) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getWorkflowStatus(appId, { cycle_id: selectedCycle });
      setGoalRows(res?.goals || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load goals data');
    } finally {
      setLoading(false);
    }
  }, [appId, selectedCycle]);

  useEffect(() => { loadGoals(); }, [loadGoals]);

  const memberName = emp => {
    if (!emp) return '—';
    return [emp.first_name, emp.last_name].filter(Boolean).join(' ') || emp.email || '—';
  };

  const filtered = useMemo(() => {
    let list = [...goalRows];
    if (scopeIds) list = list.filter(r => scopeIds.has(String(r.platform_user_id)));
    if (filterMember.length > 0) list = list.filter(r => filterMember.includes(String(r.platform_user_id)));
    if (filterStatus)            list = list.filter(r => r.goal_status === filterStatus);
    return list;
  }, [goalRows, filterMember, filterStatus, scopeIds]);

  const summary = useMemo(() => {
    const totalEmployees = filtered.length;
    const totalGoals     = filtered.reduce((s, r) => s + (r.goals_total || 0), 0);
    const approved       = filtered.reduce((s, r) => s + (r.goals_approved || 0), 0);
    const submitted      = filtered.reduce((s, r) => s + (r.goals_submitted || 0), 0);
    const draft          = filtered.reduce((s, r) => s + (r.goals_pending || 0), 0);
    const noGoals        = filtered.filter(r => !r.goals_total || r.goals_total === 0).length;
    return { totalEmployees, totalGoals, approved, submitted, draft, noGoals };
  }, [filtered]);


  const cycleName = useMemo(() => {
    const c = cycles.find(c => String(c.id) === selectedCycle);
    return c ? `${c.cycle_name} (${c.year})` : '';
  }, [cycles, selectedCycle]);

  const pdfTable = useMemo(() => ({
    headers: ['Employee', 'Goal Status', 'Total Goals', 'Approved', 'Awaiting Approval', 'Draft'],
    rows: filtered.map(r => [
      memberName(r.employee),
      GOAL_STATUS_LABEL[r.goal_status] || r.goal_status || '—',
      r.goals_total || 0,
      r.goals_approved || 0,
      r.goals_submitted || 0,
      r.goals_pending || 0,
    ]),
  }), [filtered]);

  const pdfCards = useMemo(() => !loading ? [
    { label: 'Employees',         value: summary.totalEmployees, color: 'var(--text-primary)' },
    { label: 'Total Goals',       value: summary.totalGoals,     color: 'var(--brand)'        },
    { label: 'Approved',          value: summary.approved,       color: '#4ad295'             },
    { label: 'Awaiting Approval', value: summary.submitted,      color: '#6f7bff'             },
    { label: 'Draft',             value: summary.draft,          color: '#f0a93b'             },
    { label: 'No Goals Yet',      value: summary.noGoals,        color: '#94a3b8'             },
  ] : [], [loading, summary]);

  return (
    <div className="tasks-page">
      <div className="tl-header">
        <div>
          <h1 className="page-title tl-title-row">Goals Report</h1>
          <p className="tl-subtitle">Goal submission status for all members in a selected review cycle.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => downloadExcel('goals-report.xls', 'Goals Report', cycleName ? `Review Cycle: ${cycleName}` : '', pdfTable.headers, pdfTable.rows)}
            disabled={filtered.length === 0}
            style={{ ...excelBtnStyle, opacity: filtered.length === 0 ? 0.4 : 1 }}
          >
            <ExcelIcon /> Excel
          </button>
          <ReportPdfButton
            title="Goals Report"
            subtitle={cycleName ? `Review Cycle: ${cycleName}` : ''}
            summaryCards={pdfCards}
            table={pdfTable}
            icon={<PdfIcon />}
          />
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap',
        marginBottom: 24, padding: '16px 20px',
        background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Review Cycle</label>
          <select
            value={selectedCycle}
            onChange={e => setSelectedCycle(e.target.value)}
            disabled={cyclesLoading}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, minWidth: 200 }}
          >
            {cycles.length === 0 && <option value="">No cycles</option>}
            {cycles.map(c => (
              <option key={c.id} value={String(c.id)}>{c.cycle_name} ({c.year})</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Member</label>
          <MultiSelectFilter
            options={goalRows.filter(r => !scopeIds || scopeIds.has(String(r.platform_user_id))).map(r => ({ value: String(r.platform_user_id), label: memberName(r.employee) }))}
            selected={filterMember}
            onChange={setFilterMember}
            placeholder="All Members"
            minWidth={200}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Goal Status</label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, minWidth: 180 }}
          >
            <option value="">All Statuses</option>
            {Object.entries(GOAL_STATUS_LABEL).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary cards */}
      {!loading && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
            { label: 'Employees',        value: summary.totalEmployees, color: 'var(--text-primary)' },
            { label: 'Total Goals',      value: summary.totalGoals,     color: 'var(--brand)'        },
            { label: 'Approved',         value: summary.approved,       color: '#4ad295'             },
            { label: 'Awaiting Approval',value: summary.submitted,      color: '#6f7bff'             },
            { label: 'Draft',            value: summary.draft,          color: '#f0a93b'             },
            { label: 'No Goals Yet',     value: summary.noGoals,        color: '#94a3b8'             },
          ].map(c => (
            <div key={c.label} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 10, padding: '12px 20px', minWidth: 120,
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{c.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8, padding: '12px 16px', color: '#ef4444', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {!selectedCycle && !cyclesLoading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
          No review cycles found. Create one under Organisation → Review Cycles.
        </div>
      ) : loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
          No goal data found for the selected filters.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-card)', borderBottom: '2px solid var(--border)' }}>
                <Th>Employee</Th>
                <Th>Goal Status</Th>
                <Th right>Total Goals</Th>
                <Th right>Approved</Th>
                <Th right>Awaiting Approval</Th>
                <Th right>Draft</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.platform_user_id} style={{
                  background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                  borderBottom: '1px solid var(--border)',
                }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--text-primary)', maxWidth: 220, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {memberName(r.employee)}
                  </td>
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <StatusBadge status={r.goal_status} />
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <CountBadge value={r.goals_total || 0} color="var(--brand)" />
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <CountBadge value={r.goals_approved || 0} color="#4ad295" />
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <CountBadge value={r.goals_submitted || 0} color="#6f7bff" />
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <CountBadge value={r.goals_pending || 0} color="#f0a93b" />
                  </td>
                </tr>
              ))}
            </tbody>

            {filtered.length > 1 && (
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg-card)' }}>
                  <td colSpan={2} style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'right' }}>
                    Totals
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 800, color: 'var(--brand)' }}>{summary.totalGoals}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 800, color: '#4ad295' }}>{summary.approved}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 800, color: '#6f7bff' }}>{summary.submitted}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 800, color: '#f0a93b' }}>{summary.draft}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}